// Pure helpers for the AHA-6796 CCB capacity band Phase 1: the
// context-derived per-equipment-type band the "Use structure requirements"
// toggle applies. The band comes from the admin 'sizingBands' setting when
// the API provides a valid one, with the legacy uniform slice multipliers
// (ccbCapacityBandLowerBound/UpperMultiplier) as the older-API-deploy
// fallback. SQL/UI-free so every rule is unit-testable; the toggle handler
// stays in compatible-combinations-browser-modal.tsx.

import { SizingBandsSettingType } from 'data/types';

// One filter side's multipliers (already percent / 100).
export type CcbBand = { lower: number; upper: number };

export type CcbCoolingContext = 'acCooling' | 'hpCooling' | 'ambiguous';

type SizingBandEquipmentType = SizingBandsSettingType[number]['equipmentType'];

const SIZING_BAND_EQUIPMENT_TYPES: readonly SizingBandEquipmentType[] = [
  'acCooling',
  'hpCooling',
  'heating'
];

// Tolerant read of the raw 'sizingBands' settings value — a GUI twin of the
// server's SizingBandsSchema (@alpinehomeair/common):
// an array; each row one of the three known equipmentTypes; both percents
// finite and > 0; lower <= upper; exactly one band per each of the three
// types. Anything off drops the whole record (parseSavedTonnage style) so
// the caller falls back to the legacy multipliers.
export function parseSizingBandsSetting(
  raw: unknown
): SizingBandsSettingType | null {
  if (!Array.isArray(raw)) return null;
  const bands: SizingBandsSettingType = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
      return null;
    }
    const candidate = entry as Record<string, unknown>;
    const equipmentType = candidate.equipmentType as SizingBandEquipmentType;
    if (!SIZING_BAND_EQUIPMENT_TYPES.includes(equipmentType)) return null;
    const { lowerPercent, upperPercent } = candidate;
    if (
      typeof lowerPercent !== 'number' ||
      !Number.isFinite(lowerPercent) ||
      lowerPercent <= 0 ||
      typeof upperPercent !== 'number' ||
      !Number.isFinite(upperPercent) ||
      upperPercent <= 0 ||
      lowerPercent > upperPercent
    ) {
      return null;
    }
    bands.push({ equipmentType, lowerPercent, upperPercent });
  }
  for (const equipmentType of SIZING_BAND_EQUIPMENT_TYPES) {
    if (
      bands.filter((band) => band.equipmentType === equipmentType).length !== 1
    ) {
      return null;
    }
  }
  return bands;
}

// Outdoor-unit vocabulary for the cooling-context classifier — the FULL
// ExistingSystemComponentSchema outdoor set, including the Next-Gen types
// (COMPONENT_TYPE_TO_CLASS in kept-system-type-annotation.ts) and the
// packaged units. Everything else (coils, air handlers, furnaces, blowers,
// ductless air handlers, PTAC, unknown) is not an outdoor signal.
const AC_OUTDOOR_UNIT_TYPES = new Set([
  'Split System Condenser',
  'Next-Gen Condenser',
  'Self Contained Electric AC',
  'Self Contained Gas Pack With AC'
]);

const HP_OUTDOOR_UNIT_TYPES = new Set([
  'Split System Heat Pump',
  'Next-Gen Heat Pump',
  'Self Contained Electric Heat Pump',
  'Self Contained Gas Pack With Heat Pump'
]);

type CcbContextComponent = {
  type?: string;
  replacing?: boolean;
  [key: string]: unknown;
};

// Classifies the cooling context from KEPT outdoor units only. A kept
// outdoor unit pins the pairing category: the combinations fetch is
// kept-constrained (keptComponentUniqueIds in the POST body), so every
// returned combination pairs with the customer's AC (or HP). A replaced
// outdoor unit does NOT pin — replacement browsing legitimately spans
// categories (cross-category upgrade paths are first-class). None kept, or
// kept units in BOTH categories → ambiguous; the caller envelopes the two
// cooling bands so nothing valid is hidden.
export function classifyCcbCoolingContext(
  components: CcbContextComponent[] | null | undefined
): CcbCoolingContext {
  const kept = (components ?? []).filter((component) => !component.replacing);
  const hasKeptAc = kept.some((component) =>
    AC_OUTDOOR_UNIT_TYPES.has(component.type ?? '')
  );
  const hasKeptHp = kept.some((component) =>
    HP_OUTDOOR_UNIT_TYPES.has(component.type ?? '')
  );
  if (hasKeptAc && !hasKeptHp) return 'acCooling';
  if (hasKeptHp && !hasKeptAc) return 'hpCooling';
  return 'ambiguous';
}

// Multiplier-form bands for the two capacity filter sides. With sizingBands
// present the percents become multipliers per side — the cooling side from
// the context's band, or the ENVELOPE (min of the two cooling lowers, max
// of the two uppers) when ambiguous; heating always uses the heating band.
// With sizingBands null (older API deploy / invalid record) the legacy
// uniform multipliers apply to both sides — byte-for-byte today's behavior,
// admin-tuned legacy values included.
export function resolveCcbBands(args: {
  sizingBands: SizingBandsSettingType | null;
  legacy: { lowerBound: number; upperMultiplier: number };
  coolingContext: CcbCoolingContext;
}): { cooling: CcbBand; heating: CcbBand } {
  const { sizingBands, legacy, coolingContext } = args;
  if (!sizingBands) {
    return {
      cooling: { lower: legacy.lowerBound, upper: legacy.upperMultiplier },
      heating: { lower: legacy.lowerBound, upper: legacy.upperMultiplier }
    };
  }
  // parseSizingBandsSetting guarantees exactly one row per type.
  const bandFor = (equipmentType: SizingBandEquipmentType): CcbBand => {
    const row = sizingBands.find(
      (band) => band.equipmentType === equipmentType
    ) as SizingBandsSettingType[number];
    return { lower: row.lowerPercent / 100, upper: row.upperPercent / 100 };
  };
  const heating = bandFor('heating');
  if (coolingContext === 'ambiguous') {
    const acCooling = bandFor('acCooling');
    const hpCooling = bandFor('hpCooling');
    return {
      cooling: {
        lower: Math.min(acCooling.lower, hpCooling.lower),
        upper: Math.max(acCooling.upper, hpCooling.upper)
      },
      heating
    };
  }
  return { cooling: bandFor(coolingContext), heating };
}
